// src/frontend/src/pages/IndustryPage.tsx - New component for industry-specific pages

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';

interface IndustryContent {
  title: string;
  subtitle: string;
  heroDescription: string;
  industryOverview: string;
  uniqueChallenges: string[];
  industryScenarios: {
    title: string;
    description: string;
    stakeholders: string[];
    difficulty: 'easy' | 'medium' | 'hard';
  }[];
  industryMetrics: {
    stat: string;
    description: string;
    source?: string;
  }[];
  testimonial: {
    quote: string;
    author: string;
    role: string;
    company: string;
  };
  competitors: string[];
  regulations?: string[];
  bestPractices: string[];
  ctaTitle: string;
  ctaDescription: string;
}

const industryData: Record<string, IndustryContent> = {
  'technology': {
    title: 'PM Coaching for Technology Companies',
    subtitle: 'Master agile stakeholder management in fast-paced tech environments',
    heroDescription: 'Technology project managers face unique challenges - from managing engineering teams and product roadmaps to navigating rapid pivots and technical debt discussions. Practice tech-specific stakeholder conversations with scenarios designed for software, SaaS, and tech product development.',
    industryOverview: 'Technology PMs work in dynamic environments where requirements change rapidly, technical complexity is high, and stakeholder expectations for speed and innovation are intense. Success requires balancing technical feasibility with business objectives while managing diverse stakeholder groups from engineers to executives.',
    uniqueChallenges: [
      'Explaining technical complexity to non-technical business stakeholders',
      'Managing product roadmap changes and feature prioritization',
      'Handling technical debt discussions with engineering teams',
      'Navigating agile/scrum ceremonies and stakeholder expectations',
      'Balancing feature velocity with quality and security requirements',
      'Managing customer feedback integration and product iteration cycles',
      'Coordinating between engineering, design, product, and business teams'
    ],
    industryScenarios: [
      {
        title: 'Technical Debt vs New Features',
        description: 'Engineering team wants to dedicate 40% of sprint capacity to technical debt, but executives are pushing for new revenue-generating features.',
        stakeholders: ['Engineering Manager', 'VP of Product', 'CTO'],
        difficulty: 'hard'
      },
      {
        title: 'Customer Feature Request Escalation',
        description: 'Major customer threatens to churn unless their specific feature request is prioritized, disrupting your planned roadmap.',
        stakeholders: ['Customer Success Manager', 'Enterprise Customer', 'Product Owner'],
        difficulty: 'medium'
      },
      {
        title: 'Security Vulnerability Discovery',
        description: 'Critical security vulnerability discovered that requires immediate attention, potentially delaying major product launch.',
        stakeholders: ['Security Team Lead', 'Engineering Manager', 'CEO'],
        difficulty: 'hard'
      },
      {
        title: 'API Breaking Changes Communication',
        description: 'Engineering needs to implement breaking API changes that will affect multiple customer integrations.',
        stakeholders: ['Developer Relations', 'Enterprise Customers', 'Engineering Team'],
        difficulty: 'medium'
      },
      {
        title: 'Resource Allocation for Multiple Products',
        description: 'Limited engineering resources need to be distributed across three competing product initiatives.',
        stakeholders: ['Product Managers', 'Engineering Directors', 'VP of Engineering'],
        difficulty: 'hard'
      }
    ],
    industryMetrics: [
      {
        stat: '68%',
        description: 'of tech projects fail due to poor stakeholder alignment',
        source: 'Tech Project Management Institute 2024'
      },
      {
        stat: '3.2x',
        description: 'faster feature delivery with effective PM stakeholder management',
        source: 'Developer Productivity Research'
      },
      {
        stat: '89%',
        description: 'of tech PMs report improved confidence after scenario practice',
        source: 'PM Coach User Survey'
      }
    ],
    testimonial: {
      quote: 'Working in tech means constant pivots and competing priorities. PM Coach helped me master the conversations with engineering teams about technical debt and navigate product roadmap discussions with executives. Game-changer for my effectiveness.',
      author: 'Alex Chen',
      role: 'Senior Product Manager',
      company: 'SaaS Unicorn Startup'
    },
    competitors: ['Linear', 'Notion', 'Asana', 'Jira', 'Monday.com'],
    bestPractices: [
      'Use data-driven arguments when discussing feature prioritization',
      'Translate technical concepts into business impact for executives',
      'Build strong relationships with engineering leads for technical credibility',
      'Maintain transparent communication about technical constraints and trade-offs',
      'Implement regular stakeholder alignment check-ins during rapid development cycles'
    ],
    ctaTitle: 'Master Tech PM Stakeholder Management',
    ctaDescription: 'Practice the conversations that separate successful tech PMs from those who struggle with stakeholder alignment.'
  },

  'healthcare': {
    title: 'PM Coaching for Healthcare Organizations',
    subtitle: 'Navigate complex healthcare stakeholder environments and regulatory requirements',
    heroDescription: 'Healthcare project managers work in highly regulated environments with life-critical outcomes. Master stakeholder conversations involving clinical staff, administrators, patients, and regulatory bodies while ensuring compliance and patient safety remain paramount.',
    industryOverview: 'Healthcare PMs manage projects that directly impact patient care, requiring exceptional stakeholder management skills to navigate complex organizational hierarchies, clinical workflows, regulatory requirements, and diverse stakeholder groups with varying priorities and communication styles.',
    uniqueChallenges: [
      'Managing clinical stakeholders with patient care priorities',
      'Navigating HIPAA compliance and regulatory requirements in project discussions',
      'Balancing cost containment with patient safety and quality of care',
      'Coordinating between clinical, administrative, and IT stakeholders',
      'Managing change resistance in clinical workflows and established practices',
      'Handling emergency project pivots due to patient safety concerns',
      'Communicating with patients, families, and patient advocacy groups'
    ],
    industryScenarios: [
      {
        title: 'EMR Implementation Resistance',
        description: 'Physicians are resisting new electronic medical records system, claiming it slows down patient care and increases documentation burden.',
        stakeholders: ['Chief Medical Officer', 'Attending Physicians', 'Nursing Staff'],
        difficulty: 'hard'
      },
      {
        title: 'Patient Safety vs Project Timeline',
        description: 'Quality assurance discovers potential patient safety issue in new medical device rollout just days before go-live.',
        stakeholders: ['Quality Assurance Director', 'Chief Patient Safety Officer', 'Hospital Administrator'],
        difficulty: 'hard'
      },
      {
        title: 'Budget Cuts Affecting Clinical Programs',
        description: 'Hospital administration requires 30% budget reduction for patient care improvement initiative due to reimbursement changes.',
        stakeholders: ['CFO', 'Clinical Department Directors', 'Patient Care Coordinators'],
        difficulty: 'medium'
      },
      {
        title: 'Regulatory Compliance Audit Findings',
        description: 'Joint Commission audit identifies compliance gaps in current project deliverables requiring immediate remediation.',
        stakeholders: ['Compliance Officer', 'Risk Management', 'Department Heads'],
        difficulty: 'hard'
      },
      {
        title: 'Clinical Workflow Optimization',
        description: 'Proposed efficiency improvements conflict with established clinical practices and physician preferences.',
        stakeholders: ['Clinical Staff', 'Process Improvement Team', 'Patient Experience Manager'],
        difficulty: 'medium'
      }
    ],
    industryMetrics: [
      {
        stat: '74%',
        description: 'of healthcare IT projects exceed budget due to stakeholder management issues',
        source: 'Healthcare IT Project Management Report 2024'
      },
      {
        stat: '45%',
        description: 'reduction in clinical workflow disruption with effective change management',
        source: 'Healthcare Change Management Institute'
      },
      {
        stat: '92%',
        description: 'of healthcare PMs report better clinical stakeholder relationships after training',
        source: 'PM Coach Healthcare Survey'
      }
    ],
    testimonial: {
      quote: 'Healthcare stakeholder management is incredibly complex - you\'re dealing with life-and-death decisions, regulations, and passionate clinical staff. PM Coach scenarios helped me navigate these sensitive conversations with confidence and respect.',
      author: 'Dr. Sarah Mitchell',
      role: 'Director of Clinical Informatics',
      company: 'Regional Medical Center'
    },
    competitors: ['Epic', 'Cerner', 'Allscripts', 'athenahealth', 'McKesson'],
    regulations: ['HIPAA', 'Joint Commission', 'CMS', 'FDA', 'State Health Departments'],
    bestPractices: [
      'Always prioritize patient safety in project decision discussions',
      'Understand clinical workflows before proposing changes',
      'Build trust with clinical staff through transparent communication',
      'Involve patients and families in appropriate project discussions',
      'Maintain strict confidentiality and HIPAA compliance in all communications'
    ],
    ctaTitle: 'Master Healthcare PM Communications',
    ctaDescription: 'Develop the stakeholder management skills essential for successful healthcare project delivery.'
  },

  'consulting': {
    title: 'PM Coaching for Consulting Firms',
    subtitle: 'Excel at client stakeholder management and project delivery in consulting environments',
    heroDescription: 'Consulting project managers face unique pressures - managing demanding clients, tight timelines, diverse project teams, and high visibility engagements. Master the stakeholder conversations that determine consulting project success and client satisfaction.',
    industryOverview: 'Consulting PMs operate in high-stakes environments where client relationships, project outcomes, and firm reputation are constantly on the line. Success requires exceptional stakeholder management across client organizations, internal consulting teams, and partner networks.',
    uniqueChallenges: [
      'Managing demanding and often skeptical client stakeholders',
      'Navigating client organizational politics and competing agendas',
      'Handling scope creep and change requests from multiple client contacts',
      'Managing global teams across time zones and cultural differences',
      'Balancing client expectations with realistic project constraints',
      'Dealing with client resistance to consultant recommendations',
      'Coordinating between multiple client departments and decision makers'
    ],
    industryScenarios: [
      {
        title: 'Client Executive Questioning ROI',
        description: 'Client CEO questions the value of consulting engagement halfway through project, threatening early termination.',
        stakeholders: ['Client CEO', 'Client Sponsor', 'Consulting Partner'],
        difficulty: 'hard'
      },
      {
        title: 'Competing Client Department Priorities',
        description: 'Two client departments have conflicting requirements for the same deliverable, each claiming executive support.',
        stakeholders: ['Department Head A', 'Department Head B', 'Client Project Sponsor'],
        difficulty: 'medium'
      },
      {
        title: 'Team Member Performance Issues',
        description: 'Junior consultant is underperforming in front of client, risking engagement credibility and team dynamics.',
        stakeholders: ['Client Contact', 'Junior Consultant', 'Consulting Manager'],
        difficulty: 'medium'
      },
      {
        title: 'Scope Expansion Without Budget',
        description: 'Client expects significant additional work based on initial findings but refuses to increase project budget.',
        stakeholders: ['Client Sponsor', 'Client Procurement', 'Consulting Partner'],
        difficulty: 'hard'
      },
      {
        title: 'Cultural Change Resistance',
        description: 'Client employees resist recommended organizational changes, undermining project implementation.',
        stakeholders: ['Client Change Management', 'Employee Representatives', 'Client Leadership'],
        difficulty: 'hard'
      }
    ],
    industryMetrics: [
      {
        stat: '67%',
        description: 'of consulting projects exceed timeline due to client stakeholder issues',
        source: 'Consulting Industry Project Management Report'
      },
      {
        stat: '84%',
        description: 'client satisfaction improvement with effective stakeholder management',
        source: 'Client Relationship Management Study'
      },
      {
        stat: '156%',
        description: 'higher project renewal rate for consultants with strong PM skills',
        source: 'Consulting Revenue Analysis'
      }
    ],
    testimonial: {
      quote: 'Consulting requires managing some of the most demanding stakeholders you\'ll ever encounter. PM Coach scenarios taught me how to handle skeptical executives, scope battles, and team dynamics that make or break consulting engagements.',
      author: 'Michael Rodriguez',
      role: 'Principal Consultant',
      company: 'Big Four Consulting Firm'
    },
    competitors: ['McKinsey', 'BCG', 'Bain', 'Deloitte', 'PwC', 'EY', 'KPMG', 'Accenture'],
    bestPractices: [
      'Establish clear communication protocols with client stakeholders early',
      'Document all client decisions and change requests immediately',
      'Build relationships with client influencers beyond the official sponsor',
      'Proactively communicate project value and progress to combat skepticism',
      'Manage client expectations continuously throughout the engagement'
    ],
    ctaTitle: 'Master Consulting Stakeholder Management',
    ctaDescription: 'Develop the client management skills that separate successful consultants from those who struggle with engagements.'
  },

  'financial-services': {
    title: 'PM Coaching for Financial Services',
    subtitle: 'Navigate regulatory complexity and risk-conscious stakeholders in financial organizations',
    heroDescription: 'Financial services project managers operate in heavily regulated environments where risk management, compliance, and stakeholder trust are paramount. Master conversations with regulators, risk managers, executives, and clients in high-stakes financial environments.',
    industryOverview: 'Financial services PMs manage projects that directly impact customer financial wellbeing and institutional risk. This requires sophisticated stakeholder management across regulatory bodies, risk management teams, executive leadership, and diverse customer segments.',
    uniqueChallenges: [
      'Managing regulatory compliance requirements and examiner relationships',
      'Navigating complex risk management and approval processes',
      'Handling sensitive customer data and privacy concerns',
      'Coordinating across multiple business lines and regulatory jurisdictions',
      'Managing stakeholder expectations during market volatility',
      'Balancing innovation with regulatory and risk constraints',
      'Communicating with diverse customer segments and advocacy groups'
    ],
    industryScenarios: [
      {
        title: 'Regulatory Examination Findings',
        description: 'Federal regulator identifies compliance gaps in new digital banking platform requiring immediate remediation.',
        stakeholders: ['Bank Examiner', 'Chief Risk Officer', 'Compliance Director'],
        difficulty: 'hard'
      },
      {
        title: 'Customer Data Breach Response',
        description: 'Security incident compromises customer financial data, requiring coordinated response across multiple stakeholder groups.',
        stakeholders: ['CISO', 'Customer Service Director', 'Legal Counsel'],
        difficulty: 'hard'
      },
      {
        title: 'Digital Transformation vs Risk Appetite',
        description: 'Executive pressure for rapid digital innovation conflicts with institutional risk management policies.',
        stakeholders: ['Chief Digital Officer', 'Chief Risk Officer', 'Board Risk Committee'],
        difficulty: 'medium'
      },
      {
        title: 'Customer Complaint Escalation',
        description: 'High-value customer threatens to leave due to service disruption during system upgrade, involving multiple departments.',
        stakeholders: ['Relationship Manager', 'Customer Advocate', 'Operations Director'],
        difficulty: 'medium'
      },
      {
        title: 'Cross-Border Regulatory Coordination',
        description: 'International expansion project requires coordination across multiple regulatory jurisdictions with conflicting requirements.',
        stakeholders: ['International Regulators', 'Legal Teams', 'Business Development'],
        difficulty: 'hard'
      }
    ],
    industryMetrics: [
      {
        stat: '78%',
        description: 'of fintech projects face regulatory stakeholder challenges',
        source: 'Financial Services Project Management Survey'
      },
      {
        stat: '43%',
        description: 'faster regulatory approval with effective stakeholder engagement',
        source: 'RegTech Implementation Study'
      },
      {
        stat: '91%',
        description: 'of financial services PMs report better regulatory relationships after training',
        source: 'PM Coach Financial Services Survey'
      }
    ],
    testimonial: {
      quote: 'Financial services stakeholder management is like walking a tightrope between innovation and compliance. PM Coach helped me master conversations with regulators, risk managers, and executives that I face daily in banking.',
      author: 'Jennifer Park',
      role: 'VP of Digital Projects',
      company: 'Regional Banking Corporation'
    },
    competitors: ['JPMorgan Chase', 'Bank of America', 'Wells Fargo', 'Goldman Sachs', 'Morgan Stanley'],
    regulations: ['SOX', 'Dodd-Frank', 'Basel III', 'GDPR', 'PCI DSS', 'AML/BSA'],
    bestPractices: [
      'Maintain detailed documentation for regulatory compliance and audit trails',
      'Engage risk management and compliance teams early in project planning',
      'Build relationships with key regulators and examination staff',
      'Prioritize customer protection and data security in all project decisions',
      'Communicate transparently about regulatory constraints and requirements'
    ],
    ctaTitle: 'Master Financial Services PM Skills',
    ctaDescription: 'Develop expertise in managing the complex stakeholder relationships that drive financial services project success.'
  }
};

const IndustryPage: React.FC = () => {
  const { industry } = useParams<{ industry: string }>();
  
  if (!industry || !industryData[industry]) {
    return (
      <HomeLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Industry Page Not Found</h1>
          <p className="text-gray-600 mb-8">The industry you're looking for doesn't exist.</p>
          <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Back to Home
          </Link>
        </div>
      </HomeLayout>
    );
  }

  const data = industryData[industry];

  return (
    <HomeLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="py-16 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6">
            {data.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {data.subtitle}
          </p>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
            {data.heroDescription}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-lg"
          >
            Start Free Trial
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Industry Overview */}
        <div className="py-16 bg-gray-50 rounded-2xl mb-16">
          <div className="max-w-4xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
              Industry Overview
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              {data.industryOverview}
            </p>
          </div>
        </div>

        {/* Unique Challenges */}
        <div className="py-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Unique {industry.charAt(0).toUpperCase() + industry.slice(1)} PM Challenges
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.uniqueChallenges.map((challenge, index) => (
                <div key={index} className="flex items-start bg-blue-50 p-4 rounded-lg">
                  <svg className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{challenge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Industry-Specific Scenarios */}
        <div className="py-16 bg-purple-50 rounded-2xl">
          <div className="max-w-6xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Practice Real {industry.charAt(0).toUpperCase() + industry.slice(1)} Scenarios
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.industryScenarios.map((scenario, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-gray-900 text-lg">{scenario.title}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      scenario.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                      scenario.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {scenario.difficulty.charAt(0).toUpperCase() + scenario.difficulty.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm">{scenario.description}</p>
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Key Stakeholders:</h4>
                    <div className="flex flex-wrap gap-1">
                      {scenario.stakeholders.map((stakeholder, idx) => (
                        <span key={idx} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {stakeholder}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Industry Metrics */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Industry Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {data.industryMetrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{metric.stat}</div>
                <p className="text-gray-600 mb-2">{metric.description}</p>
                {metric.source && (
                  <p className="text-xs text-gray-500">Source: {metric.source}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="py-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
          <div className="max-w-4xl mx-auto text-center px-8">
            <blockquote className="text-2xl font-medium text-gray-900 mb-6">
              "{data.testimonial.quote}"
            </blockquote>
            <div className="text-gray-600">
              <div className="font-semibold">{data.testimonial.author}</div>
              <div>{data.testimonial.role} at {data.testimonial.company}</div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              {industry.charAt(0).toUpperCase() + industry.slice(1)} PM Best Practices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.bestPractices.map((practice, index) => (
                <div key={index} className="flex items-start bg-green-50 p-4 rounded-lg">
                  <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 text-sm">{practice}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{data.ctaTitle}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {data.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600"
            >
              Talk to Industry Expert
            </Link>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            14-day free trial • No credit card required • Industry-specific scenarios included
          </p>
        </div>
      </div>
    </HomeLayout>
  );
};

export default IndustryPage;